import { creatorApiClient } from './apiClient';

const API_BASE = '/ai-question';

/**
 * Grammar point for context
 */
export interface GrammarPoint {
  title: string;
  explanation: string;
}

/**
 * Speaking chunk for context
 */
export interface Chunk {
  text: string;
  meaning: string;
}

/**
 * Request payload for asking AI a question
 * Frontend sends all available context; backend decides what to use based on question type
 */
export interface AskQuestionRequest {
  question: string;
  context?: {
    sentence?: string;
    translation?: string;  // Chinese translation
    vocabulary?: string[];
    grammarPoints?: GrammarPoint[];
    chunks?: Chunk[];  // Speaking strategy chunks
    courseId?: string;
  };
}

/**
 * Token usage information from AI question
 */
export interface TokenUsage {
  wasFree: boolean;        // Whether this question was within the free daily limit
  tokensSpent: number;     // Tokens spent (0 if free)
  freeRemaining: number;   // Remaining free questions for today
  newBalance: number;      // User's new token balance
}

/**
 * Response from AI question endpoint
 */
export interface AskQuestionResponse {
  id: string;
  answer: string;
  model: string;
  complexity: string;
  responseTimeMs: number;
  cost: {
    inputTokens: number;
    outputTokens: number;
    estimatedCost: number;
  };
  tokenUsage?: TokenUsage;
}

/**
 * Rating value: 1 for positive, -1 for negative
 */
export type RatingValue = 1 | -1;

/**
 * AI Question Service
 * Handles API calls for AI-powered Q&A functionality
 */
export const aiQuestionService = {
  /**
   * Ask the AI a question with optional learning context
   * @param request - The question and optional context
   * @returns AI response with answer, model info, and cost
   */
  async ask(request: AskQuestionRequest): Promise<AskQuestionResponse> {
    const response = await creatorApiClient.post<AskQuestionResponse>(
      `${API_BASE}/ask`,
      request
    );
    return response.data;
  },

  /**
   * Rate an AI answer
   * @param id - The question log ID
   * @param rating - 1 for positive (thumbs up), -1 for negative (thumbs down)
   */
  async rate(id: string, rating: RatingValue): Promise<{ success: boolean }> {
    const response = await creatorApiClient.post<{ success: boolean }>(
      `${API_BASE}/${id}/rate`,
      { rating }
    );
    return response.data;
  },
};

export default aiQuestionService;
